import { ReactElement, useMemo, useState } from "react";

import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from "@chakra-ui/react";

import { DownloadIcon } from "icons";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import useDownloadFile from "hooks/useDownloadFile";
import { formatDate } from "util/statHelpers";
import Empty from "./Empty";

const FileModalDownloadLink = ({ file }: { file: DocumentsFile }) => {
  const [isLoading, setIsLoading] = useState(false);
  const downloadDocumentFile = useDownloadFile();
  return (
    <HStack>
      <Button
        variant="link"
        onClick={() => {
          setIsLoading(true);
          downloadDocumentFile(file)?.finally(() => setIsLoading(false));
        }}
        textAlign="left"
        wordBreak="break-all"
        whiteSpace="break-spaces"
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        <HStack spacing={1} align="flex-start">
          <Text color="inherit" fontWeight="semibold" textStyle="body2">
            {file.filename}
          </Text>
          <DownloadIcon />
        </HStack>
      </Button>
      <Spacer flex={1} />
      <Text textStyle="body2" color="grey.500">
        {formatDate(file.date as Date)}
      </Text>
    </HStack>
  );
};

const FileModal = ({
  heading = "Files",
  files,
  isOpen = false,
  onClose = () => {
    return;
  },
}: {
  heading?: string;
  files?: DocumentsFile[];
  isOpen?: boolean;
  onClose?(): void;
}): ReactElement => {
  const filesList = useMemo(() => {
    if (!files) {
      return <LoadingSpinner />;
    }
    if (files.length <= 0) {
      return (
        <Center minHeight="400px">
          <Empty message="No files yet." />
        </Center>
      );
    }
    return files.map((file) => (
      <FileModalDownloadLink key={file.filename} file={file} />
    ));
  }, [files]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent p={1}>
        <ModalHeader textStyle="h4">{heading}</ModalHeader>
        <ModalCloseButton aria-label="close" />
        <ModalBody pb={7} pt={3}>
          {filesList}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default FileModal;
